import { IBasePicker, ITag, TagPicker, ValidationState } from "@fluentui/react";
import {
  Button,
  Field,
  FluentProvider,
  Input,
  Label,
  Text,
  Textarea,
  makeStyles,
  webLightTheme,
} from "@fluentui/react-components";

import { useBoolean } from "@fluentui/react-hooks";
import React, { useEffect, useRef, useState } from "react";
import CurrencyInput from "react-currency-input-field";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import classNames from "classnames";
import { AddItemFormInput, addItemFormsDefault, menuItemDbToFormData } from "../../common/draftMenuHelper";
import { addItemToBank } from "../../reducers/diningReducer";
import {
  cafeAllergenBankSelector,
  cafeSelector,
  currencySelector,
  selectedItemSelector,
  stationSelector,
  weekStartStringSelector,
} from "../../selectors/diningSelector";
import { useAppDispatch } from "../../store/store";
import styles from "../../styles/addItem.module.css";
import withAppInsights from "../AppInsights/AppInsights";
import { ImageCropForm } from "./ImageCropForm";
export interface IMenuItemImage {
  file: File;
  imagePreviewUrl?: string;
}

interface AddItemFormProps {
  closePanel: () => void;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  tagGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
  },
});

const AddItemForm = ({ closePanel }: AddItemFormProps) => {
  const selectedStation = useSelector(stationSelector);
  const selectedCafe = useSelector(cafeSelector);
  const currentWeek = useSelector(weekStartStringSelector);
  const selectedItem = useSelector(selectedItemSelector);
  const allergenBank = useSelector(cafeAllergenBankSelector);
  const currentCurrency = useSelector(currencySelector);
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const item = selectedItem ? menuItemDbToFormData(selectedItem) : addItemFormsDefault;
  const { register, control, handleSubmit, setValue, getValues, watch } = useForm<AddItemFormInput>({
    defaultValues: item,
    mode: "onChange",
  });
  const image = watch("image");
  const browseButtonRef = useRef<HTMLButtonElement | null>(null);
  const deleteButtonRef = useRef<HTMLButtonElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const [tempImage, setTempImage] = useState<IMenuItemImage | null>(null);
  const [imageMessage, setImageMessage] = useState<string | null>("");
  const [croppedMenuItemImage, setCroppedMenuItemImage] = useState<IMenuItemImage>();
  const [cropComplete, { setTrue: setCropComplete, setFalse: resetCropComplete }] = useBoolean(false);
  const [fileName, setFileName] = useState<string>();
  const [isModalOpen, { setTrue: openModal, setFalse: closeModal }] = useBoolean(false);
  //const [deleteImageButton, { setTrue: showDeleteImageButton, setFalse: hideDeleteImageButton }] = useBoolean(false);
  // -------------------
  // Allergen Picker
  // -------------------
  let allergenList: ITag[] = [];
  allergenBank?.forEach((a) => a.name && a.id && allergenList.push({ name: a.name, key: a.id } as ITag));

  const addToAllergenList = (newWord: string) => {
    const newAllergen: ITag = { name: newWord, key: uuidv4() };
    allergenList.push(newAllergen);
    return newAllergen;
  };

  const selectedAllergens = watch("allergens", item.allergens);
  const getTextFromItem = (item: ITag) => item.name;
  const previousImageRef = useRef<AddItemFormInput["image"] | null>(null);
  let optionalAllergensInputRef = useRef<IBasePicker<ITag> | null>(null);
  const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
    if (!tagList || !tagList.length) return false;
    return tagList.some((compareTag) => compareTag.key === tag.key);
  };
  const filterOptionalSuggestedTags = (filterText: string, tagList: ITag[]): ITag[] => {
    return filterText && selectedAllergens.length > 0
      ? selectedAllergens.filter(
          (tag) =>
            tag.name?.toLowerCase().indexOf(filterText.toLowerCase()) === 0 && !listContainsTagList(tag, tagList),
        )
      : [];
  };
  useEffect(() => {
    if (cropComplete && croppedMenuItemImage) {
      const file = croppedMenuItemImage.file;
      const previewUrl = URL.createObjectURL(file);
      setFileName(`${tempImage?.file?.name}`);
      setValue("image", { file: file, previewUrl, imageUrl: undefined }, { shouldDirty: true });
      setImageMessage(`"${tempImage?.file?.name}" added successfully`);
      setTimeout(() => {
        closeModal();
      }, 100);
      setTimeout(() => {
        deleteButtonRef.current?.focus();
      }, 100);
    }
  }, [cropComplete, croppedMenuItemImage]);
  // Restore focus only AFTER modal closes, and only if relevant

  const resetImageRefAndState = () => {
    resetCropComplete();
    setCroppedMenuItemImage(undefined);
  };
  const onBrowseClick = (
    e: React.MouseEvent<
      HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | HTMLSpanElement | typeof Button,
      MouseEvent
    >,
  ) => {
    imageInputRef?.current?.click();
  };
  const onImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageMessage("");
    if (cropComplete && croppedMenuItemImage) {
      resetCropComplete();
    }
    if (e.target.files && e.target.files.length > 0) {
      previousImageRef.current = getValues("image");
      const file = e.target.files[0];
      const imagePreviewUrl = URL.createObjectURL(file);
      setTempImage({ file, imagePreviewUrl });
      setTimeout(() => {
        openModal();
      }, 100);
    }
  };

  const clearImage = () => {
    const img = getValues("image");
    if (img?.previewUrl) URL.revokeObjectURL(img.previewUrl);

    setValue("image", { ...addItemFormsDefault.image, imageUrl: item.image?.imageUrl });
    setTempImage(null);
    resetImageRefAndState();
    if (imageInputRef.current) imageInputRef.current.value = "";
  };
  const cancelImageCrop = () => {
    resetCropComplete();
    setTempImage(null);
    if (previousImageRef.current?.file !== undefined) {
      setValue("image", previousImageRef.current, { shouldDirty: false });
    }

    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };
  const onImageDelete = () => {
    clearImage();
    if (fileName !== undefined) {
      setImageMessage(`"${fileName}" removed successfully`);
    } else {
      setImageMessage(`Image removed successfully`);
    }
    setValue("image", { file: undefined, previewUrl: undefined, imageUrl: undefined }, { shouldDirty: true });
    setTimeout(() => browseButtonRef.current?.focus(), 100);
  };

  // -------------------
  // Allergens suggestions
  // -------------------
  const _onResolveSuggestions = (filterText: string) => {
    const suggestionsFromBank = allergenList
      ?.filter((a) => a.name?.toLowerCase().startsWith(filterText.toLowerCase()))
      .map(({ name }) => ({ key: name, name }));
    if (suggestionsFromBank && suggestionsFromBank.length) return suggestionsFromBank;

    // Free form input
    const titleCaseSuggestion = filterText
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.substr(1).toLowerCase())
      .join(" ");
    return [{ key: uuidv4(), name: titleCaseSuggestion }];
  };

  // -------------------
  // Submit
  // -------------------
  const onSubmit = (data: AddItemFormInput) => {
    data.id = data.id || uuidv4();
    const edited = !!selectedItem;

    dispatch(addItemToBank(selectedCafe?.id ?? "", selectedStation?.id ?? "", data, currentWeek, edited));
    closePanel();
  };

  return (
    <FluentProvider theme={webLightTheme} targetDocument={typeof window !== "undefined" ? window.document : undefined}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          {/* NAME */}
          <Controller
            name="name"
            control={control}
            rules={{ required: "Required field." }}
            render={({ field, fieldState }) => {
              const hasError = !!fieldState.error;
              return (
                <Field
                  label="Item name"
                  required
                  validationState={hasError ? "error" : "none"}
                  validationMessage={fieldState.error?.message}
                >
                  <Input
                    {...field}
                    data-testid="ItemNameInput"
                    placeholder="Menu Item Name"
                    aria-label={"Menu item name, required"}
                  />
                </Field>
              );
            }}
          />

          {/* DESCRIPTION */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* DESCRIPTION */}
            <Controller
              name="description"
              control={control}
              rules={{ required: "Required field." }}
              render={({ field, fieldState }) => (
                <Field
                  label="Item description"
                  required
                  validationState={fieldState.error ? "error" : "none"}
                  validationMessage={fieldState.error?.message}
                >
                  <Textarea
                    {...field}
                    placeholder="Add your description"
                    data-testid="ItemDescriptionTextArea"
                    className={styles.descriptionField}
                    aria-label={"Menu item description, required"}
                  />
                </Field>
              )}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Label>Category</Label>
            <Input
              {...register("category")}
              defaultValue={item.category}
              data-testid="ItemCategoryTextArea"
              aria-label={"Category"}
            />
          </div>
          <div className={styles.row}>
            <Controller
              control={control}
              name="price"
              defaultValue={item.price}
              data-testid={"ItemPriceInput"}
              rules={{
                required: { value: true, message: "Please enter a valid price." },
              }}
              render={({ field, fieldState }) => (
                <Field
                  label="Price:"
                  validationState={fieldState.error ? "error" : "none"}
                  validationMessage={fieldState.error?.message}
                >
                  <CurrencyInput
                    data-testid={"ItemPriceInput"}
                    placeholder={"Price"}
                    name={field.name}
                    allowNegativeValue={false}
                    intlConfig={{ locale: "en-US", currency: currentCurrency.code }}
                    value={field.value}
                    onValueChange={(value) => field.onChange(value ?? "")}
                    style={{ height: "32px" }}
                    className={classNames(styles.priceInput, styles.fluentInput, fieldState.error && styles.inputError)}
                    decimalsLimit={10}
                  />
                </Field>
              )}
            />
            <Controller
              name="calories"
              control={control}
              render={({ field, fieldState }) => (
                <Field
                  label="Calories"
                  validationState={fieldState.error ? "error" : "none"}
                  validationMessage={fieldState.error?.message}
                >
                  <Input
                    {...register("calories", {
                      pattern: {
                        value: /^[1-9]{0,1}[0-9]{0,5}$/,
                        message: "Positive values only. Leave blank if unknown.",
                      },
                    })}
                    aria-label={"Number of calories, optional."}
                    defaultValue={item.calories}
                    data-testid={"ItemCaloriesInput"}
                    placeholder="Unknown"
                  />
                </Field>
              )}
            />
          </div>
          {/* ALLERGENS */}
          <div className={classes.container}>
            <Label htmlFor="containsAllergensPicker">Contains allergens:</Label>
            <Controller
              key={"allergens"}
              name={"allergens"}
              control={control}
              defaultValue={item.allergens}
              render={({ field: { onChange } }) => (
                <TagPicker
                  defaultSelectedItems={item.allergens}
                  className={styles.allergenStackPicker}
                  removeButtonAriaLabel="Remove"
                  onChange={onChange}
                  aria-label="Includes the following allergens:"
                  onResolveSuggestions={_onResolveSuggestions}
                  getTextFromItem={(a: ITag) => a.name}
                  createGenericItem={addToAllergenList}
                  pickerSuggestionsProps={{
                    suggestionsHeaderText: "Matching allergens",
                    noResultsFoundText: "No allergens found",
                  }}
                  itemLimit={100}
                  inputProps={{ "data-testid": "ItemAllergensContain", id: "containsAllergensPicker" } as any}
                  onValidateInput={(input: string) => (input ? ValidationState.valid : ValidationState.invalid)}
                />
              )}
            />
          </div>

          {/* OPTIONAL ALLERGENS */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Label htmlFor="optionalAllergensPicker">Can be made without:</Label>
            <Controller
              name="optionalAllergens"
              control={control}
              defaultValue={item.optionalAllergens}
              render={({ field }) => (
                <TagPicker
                  defaultSelectedItems={item.optionalAllergens}
                  removeButtonAriaLabel="Remove allergen"
                  onResolveSuggestions={filterOptionalSuggestedTags}
                  onChange={field.onChange}
                  getTextFromItem={getTextFromItem}
                  componentRef={(ref) => {
                    optionalAllergensInputRef.current = ref;
                    register;
                  }}
                  pickerSuggestionsProps={{
                    suggestionsHeaderText: "Suggested allergens",
                    noResultsFoundText: "No allergens found",
                  }}
                  inputProps={{ "data-testid": "ItemAllergensOptional", id: "optionalAllergensPicker" } as any}
                  disabled={selectedAllergens.length === 0 && item.optionalAllergens.length === 0}
                />
              )}
            />
          </div>

          {/* IMAGE UPLOAD */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Label>Add an Image</Label>
            {imageMessage && <Text role="status">{imageMessage}</Text>}
            {/* Preview */}
            {image?.previewUrl && (
              <img
                src={image.previewUrl}
                width={238}
                alt="Preview of uploaded menu item image"
                data-testid="ImageForm"
              />
            )}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
              <Button ref={browseButtonRef} onClick={onBrowseClick}>
                Browse
              </Button>

              {(image?.previewUrl || image?.imageUrl) && (
                <Button ref={deleteButtonRef} onClick={onImageDelete} data-testid="ImageDeleteBtn">
                  Delete
                </Button>
              )}

              <input
                type="file"
                accept="image/jpg,image/jpeg,image/png"
                ref={imageInputRef}
                onChange={onImageChange}
                style={{ display: "none" }}
              />
            </div>

            <ImageCropForm
              isModalOpen={isModalOpen}
              closeModal={closeModal}
              tempImage={tempImage}
              setCropImage={setCroppedMenuItemImage}
              setCropComplete={setCropComplete}
              onCancel={cancelImageCrop}
            />
          </div>

          {/* SUBMIT */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button appearance="primary" type="submit" data-testid={"AddSubmitBtn"}>
              {selectedItem ? "Save" : "Add to menu bank"}
            </Button>
          </div>
        </div>
      </form>
    </FluentProvider>
  );
};

export default withAppInsights(AddItemForm);
